import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { GoalSpinResultModal } from "@/apps/goals/components/GoalSpinResultModal/GoalSpinResultModal";
import { ParticipationLockedModal } from "@/apps/goals/components/ParticipationLockedModal/ParticipationLockedModal";
import { WinnersTicker } from "@/apps/goals/components/WinnersTicker/WinnersTicker";
import { Wheel } from "@/apps/goals/components/Wheel/Wheel";
import { useGoalsStore } from "@/apps/goals/store/goalsStore";
import { GoalTemplate } from "@/apps/goals/types";
import { formatGoalRewardAmount } from "@/apps/goals/utils/formatTime";
import { getGoalsScreenUrl } from "@/apps/goals/utils/goalsRoutes";
import { useStartupParamsStore } from "@/store/startupParams";
import Typography from "@/ui/Typography/Typography";
import { useServiceT } from "@/utils/hooks/useServiceT";
import cls from "./styles.module.scss";

export const WheelPage = () => {
  const { t } = useTranslation();
  const serviceT = useServiceT();
  const navigateTimeoutRef = useRef<number>(null);
  const templates = useGoalsStore(
    (state) => state.initData?.program.templates ?? []
  );
  const rewardAmount = useGoalsStore(
    (state) => state.initData?.program.goal_price ?? 0
  );
  const canParticipate = useGoalsStore(
    (state) => state.initData?.can_participate
  );

  const spinGoal = useGoalsStore((state) => state.spinGoal);
  const activateGoal = useGoalsStore((state) => state.activateGoal);
  const yandexId = useStartupParamsStore((state) => state.auth.yandexId);
  const serviceType = useStartupParamsStore((state) => state.serviceType);
  const hasRewardRange = serviceType === "eats" || serviceType === "delivery";
  const [isGoalModalOpen, setIsGoalModalOpen] = useState(false);
  const [isParticipationModalOpen, setIsParticipationModalOpen] = useState(
    false
  );
  const [isLeaving, setIsLeaving] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<GoalTemplate | null>(
    null
  );
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(
    () => () => {
      if (navigateTimeoutRef.current) {
        window.clearTimeout(navigateTimeoutRef.current);
      }
    },
    []
  );

  const handleSpin = () => {
    if (canParticipate === false) {
      setIsParticipationModalOpen(true);
      return Promise.reject();
    }

    if (!yandexId) {
      return Promise.reject();
    }

    return spinGoal(yandexId);
  };

  const handleSpinComplete = (template: GoalTemplate) => {
    setSelectedTemplate(template);
    setIsGoalModalOpen(true);
  };

  const continueToActiveGoal = () => {
    setIsGoalModalOpen(false);
    setIsLeaving(true);

    navigateTimeoutRef.current = window.setTimeout(() => {
      activateGoal();
      navigate({
        pathname: getGoalsScreenUrl("activeGoal"),
        search: location.search,
      });
    }, 320);
  };

  return (
    <section className={[cls.root, isLeaving ? cls.rootLeaving : ""].join(" ")}>
      <div className={cls.content}>
        {canParticipate === false && (
          <img
            className={cls.preloadImage}
            src="/goals/goals_with_multi.png"
            alt=""
            aria-hidden="true"
          />
        )}
        <Typography
          className={cls.title}
          component="h1"
          size="3xl"
          weight="bold"
          family="custom"
        >
          {t("goals.wheel.titleStart")} {t("goals.wheel.titleEnd")}
          <br />
          <span className={cls.reward}>
            {hasRewardRange
              ? serviceT("goals.wheel.rewardRange")
              : t("goals.wheel.reward", {
                  price: formatGoalRewardAmount(rewardAmount),
                })}
          </span>
        </Typography>
        <WinnersTicker />
        <Wheel
          templates={templates}
          onSpin={handleSpin}
          onSpinComplete={handleSpinComplete}
        />
      </div>
      <GoalSpinResultModal
        isOpen={isGoalModalOpen}
        rides={selectedTemplate?.rides_required ?? null}
        rewardAmount={selectedTemplate ? rewardAmount : null}
        endsAt={
          selectedTemplate?.current_assignment?.ends_at
            ? new Date(selectedTemplate.current_assignment.ends_at).getTime()
            : undefined
        }
        onConfirm={continueToActiveGoal}
      />
      <ParticipationLockedModal
        isOpen={isParticipationModalOpen}
        onClose={() => setIsParticipationModalOpen(false)}
      />
    </section>
  );
};
